import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ReactComponent as ReceiveIcon } from '@/assets/icons/receive.svg';
import { ReactComponent as SendIcon } from '@/assets/icons/send.svg';
import { Label, PrimaryButton, Space } from '@/components';
import { useFirstRenderEffect } from '@/hooks';
import { MainLayout } from '@/layout';
import { DemoUIState } from '@/model/demo/types';
import { makeSelectIsFrameView, makeSelectIsMobileView } from '@/model/global/selectors';
import { reloadBalance } from '@/model/wallets/actions';
import { makeSelectSelectedWalletsBalance, makeSelectWallets } from '@/model/wallets/selectors';
import { Wallet } from '@/model/wallets/types';
import { WalletValue } from '@/pages/wallets/components';
import { SingleWalletOption } from '@/pages/withdrawal/components';

const useStyles = makeStyles((theme: Theme) => ({
  bodyBox: {
    paddingTop: theme.spacing(3),
  },
  header: {
    backgroundColor: theme.colors.lightBackground,
  },
  headerMobile: {
    backgroundColor: theme.colors.primary,
  },
  containerMobile: {
    backgroundColor: theme.colors.light,
  },
}));

const selectWallets = makeSelectWallets();
const selectSelectBalance = makeSelectSelectedWalletsBalance();
const selectIsMobile = makeSelectIsMobileView();
const selectIsFrame = makeSelectIsFrameView();

export const WalletPage: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const { all: wallets } = useSelector(selectWallets);
  const isMobile = useSelector(selectIsMobile);
  const isFrame = useSelector(selectIsFrame);
  const balance = useSelector(selectSelectBalance);
  const dispatch = useDispatch();
  const interval = useRef<number | undefined>();
  useFirstRenderEffect(() => {
    const refreshData = (): void => {
      reloadBalance({})(dispatch);
    };
    refreshData();
    interval.current = setInterval(refreshData, 3000);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  });

  return (
    <MainLayout
      height={450}
      minHeight={400}
      content={{ className: isMobile ? classes.containerMobile : undefined }}
      footer={{ className: isMobile ? classes.containerMobile : undefined }}
      buttons={{
        logout: (
          <FormattedMessage
            id={isFrame ? 'demo.pages.wallet.logout.button.title.iframe' : 'demo.pages.wallet.logout.button.title.main'}
          />
        ),
        main: {
          submit: false,
          type: 'secondary',
          title: <FormattedMessage id="demo.pages.wallet.submit.title" />,
        },
      }}
      header={{
        className: isMobile ? classes.headerMobile : classes.header,
        postfix: (
          <WalletValue
            value={balance}
            options={[
              <PrimaryButton
                key="send"
                startIcon={<SendIcon />}
                title={<FormattedMessage id="demo.pages.wallet.options.send" />}
                onClick={async (): Promise<void> => {
                  history.push(`${DemoUIState.SEND_OPTIONS}`);
                }}
              />,
              <PrimaryButton
                key="receive"
                startIcon={<ReceiveIcon />}
                title={<FormattedMessage id="demo.pages.wallet.options.receive" />}
              />,
            ]}
          />
        ),
      }}
    >
      <Space direction="vertical" size={1.5} className={classes.bodyBox}>
        <Label>
          <FormattedMessage id="demo.pages.wallet.label" />
        </Label>
        {wallets.length > 0 && (
          <Space direction="vertical" size={2.5} style={{ overflow: 'auto' }}>
            {wallets.map((wallet: Wallet) => (
              <SingleWalletOption
                key={wallet.id}
                {...wallet}
                disabled
                onSelect={(): void => history.push(`${DemoUIState.TRANSACTIONS}/${wallet.id}`)}
              />
            ))}
          </Space>
        )}
      </Space>
    </MainLayout>
  );
};

export default WalletPage;
